import {
  ESButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  ESSelect,
  useESSnackbar,
} from '@energy-stacks/core/ui';
import { Dialog, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ChargingStationDetailsModel,
  changeAvailabilityRequestOptions,
  useChangeChargingStationAvailabilityMutation,
  ChangeAvailabilityRequestOption,
  ConnectorModel,
} from '@energy-stacks/broker/feature-charging-stations-data';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ChangeConnectorAvailabilityStatusDialogProps {
  isOpen: boolean;
  onClose: () => void;
  identityKey: ChargingStationDetailsModel['identityKey'];
  currentStatus: ConnectorModel['connectorStatus'];
  connectorId: number;
  testId?: string;
}

export const ChangeConnectorAvailabilityStatusDialog: React.FC<
  ChangeConnectorAvailabilityStatusDialogProps
> = ({ identityKey, currentStatus, connectorId, isOpen, onClose, testId }) => {
  const { t } = useTranslation('chargingStations');
  const { showSnackbar } = useESSnackbar();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
  } = useForm<{ availability: ChangeAvailabilityRequestOption }>({
    resolver: yupResolver(
      yup.object().shape({
        availability: yup
          .string()
          .required()
          .oneOf([...changeAvailabilityRequestOptions]),
      })
    ),
    mode: 'onChange',
    defaultValues: {
      availability: undefined,
    },
  });
  const [triggerChangeConnectorAvailability, { isLoading }] =
    useChangeChargingStationAvailabilityMutation();
  const requestRef =
    useRef<ReturnType<typeof triggerChangeConnectorAvailability>>();

  const onSubmit = useCallback(() => {
    const changeAvailability = triggerChangeConnectorAvailability({
      identityKey: identityKey,
      availability: getValues().availability,
      connectorId,
    });

    requestRef.current = changeAvailability;

    changeAvailability
      .unwrap()
      .then((result) => {
        if (result.status === 'Rejected') {
          showSnackbar('error', t('changeConnectorStatusRejectedMessage'));
          return;
        }

        if (result.status === 'Accepted') {
          showSnackbar('success', t('changeConnectorStatusSuccessMessage'));
        }

        if (result.status === 'Scheduled') {
          showSnackbar('info', t('changeConnectorStatusScheduledMessage'));
        }

        onClose();
      })
      .catch((error) => {
        if (error.data?.errorCode === 'RPC_TIMEOUT') {
          showSnackbar('error', t('rpcTimeoutError'));
          return;
        }

        if (error.data?.errorCode === 'RPC_ERROR') {
          showSnackbar('error', t('rpcError'));
          return;
        }

        if (error.name === 'AbortError') {
          return;
        }

        showSnackbar('error');
      });
  }, [
    identityKey,
    connectorId,
    triggerChangeConnectorAvailability,
    getValues,
    onClose,
    t,
    showSnackbar,
  ]);

  const handleCancel = useCallback(() => {
    requestRef.current?.abort();
    onClose();
  }, [onClose]);

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <ESDialogTitle>{t('changeStatus')}</ESDialogTitle>

      <ESDialogContent>
        <Stack direction="column">
          <Typography variant="caption" color="text.secondary">
            {t('currentStatus')}
          </Typography>
          <Typography>
            {currentStatus
              ? t(currentStatus, { context: 'status' })
              : t('unknown')}
          </Typography>
        </Stack>

        <Controller
          control={control}
          name="availability"
          render={({ field: { value, onChange, onBlur } }) => (
            <ESSelect
              testId={`changeStatus${testId}`}
              label={t('newStatus')}
              options={changeAvailabilityRequestOptions.map((option) => ({
                value: option,
                label: t(`changeAvailabilityRequestStatusOption.${option}`),
              }))}
              onChange={onChange}
              onBlur={onBlur}
              value={value || ''}
              required
              disabled={isLoading}
              sx={{ mt: 8 }}
            />
          )}
        />
      </ESDialogContent>

      <ESDialogActions>
        <ESButton
          data-testid={`changeStatus${testId}CancelButton`}
          variant="text"
          color="error"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </ESButton>

        <ESButton
          data-testid={`changeStatus${testId}ChangeButton`}
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {t('change')}
        </ESButton>
      </ESDialogActions>
    </Dialog>
  );
};
